import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Check, X } from 'lucide-react';

const AcceptInvitationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { acceptInvitation } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      toast.error('Invalid invitation link');
      navigate('/login');
    }
  }, [token, navigate]);

  const passwordChecks = {
    length: formData.password.length >= 8,
    lowercase: /[a-z]/.test(formData.password),
    uppercase: /[A-Z]/.test(formData.password),
    number: /[0-9]/.test(formData.password)
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (!passwordChecks.length || !passwordChecks.lowercase || !passwordChecks.uppercase || !passwordChecks.number) {
      toast.error('Password does not meet requirements');
      return;
    }

    setLoading(true);

    try {
      await acceptInvitation(token, formData.password, formData.confirmPassword);
      toast.success('Account activated! You can now log in.');
      navigate('/login');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to accept invitation. The link may have expired.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const CheckItem = ({ checked, label }) => (
    <div className="flex items-center gap-2 text-sm">
      {checked ? (
        <Check className="w-4 h-4 text-green-500" />
      ) : (
        <X className="w-4 h-4 text-red-500" />
      )}
      <span className={checked ? 'text-green-700' : 'text-[#5A5A5A]'}>{label}</span>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] p-8">
      <div className="w-full max-w-md bg-white rounded-lg shadow-md p-8">
        <div className="flex justify-center mb-6">
          <img 
            src="/ors-logo.svg" 
            alt="Onsite Rail Solutions" 
            className="h-12 w-auto"
            srcSet="/ors-logo.svg 1x, /ors-logo@2x.png 2x"
            data-testid="accept-invitation-logo"
          />
        </div>

        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-[#323033] mb-2">Welcome to Onsite Rail Solutions</h1>
          <p className="text-[#5A5A5A]">Set up your password to activate your account</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="password">Create Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                data-testid="invitation-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#808080] hover:text-[#323033]"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {formData.password && (
              <div className="mt-2 grid grid-cols-2 gap-1">
                <CheckItem checked={passwordChecks.length} label="8+ characters" />
                <CheckItem checked={passwordChecks.lowercase} label="Lowercase" />
                <CheckItem checked={passwordChecks.uppercase} label="Uppercase" />
                <CheckItem checked={passwordChecks.number} label="Number" />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              placeholder="Confirm your password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              required
              data-testid="invitation-confirm-password-input"
            />
            {formData.confirmPassword && formData.password !== formData.confirmPassword && (
              <p className="text-sm text-red-500">Passwords do not match</p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full bg-[#005EF8] hover:bg-[#0047C2] text-white h-11"
            disabled={loading}
            data-testid="accept-invitation-btn"
          >
            {loading ? 'Activating...' : 'Activate Account'}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-[#5A5A5A]">
          Already have an account?{' '}
          <Link
            to="/login"
            className="text-[#005EF8] hover:text-[#0047C2] font-medium"
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default AcceptInvitationPage;
